import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useFhirData } from "@/utils/useFhirData";
import { useFormatter } from "@/utils/useFormatter";

/**
 * Interface para informações do pet
 */
export interface PetInfo {
  id: string;
  name: string;
  species: string;
  breed: string;
  age?: string;
}

/**
 * Interface para informações do tutor
 */
export interface TutorInfo {
  name: string;
  document?: string;
  phone?: string;
}

/**
 * Interface para informações da clínica
 */
export interface ClinicInfo {
  clinicId: string;
  clinicName: string;
  clinicAddress: string;
  clinicPhone: string;
  vetName: string;
  vetCRMV: string;
}

/**
 * Hook customizado para centralizar a lógica de extração de informações
 * do pet, tutor e clínica para geração de documentos veterinários
 *
 * @returns Objeto contendo petInfo, tutorInfo e clinicInfo formatados
 */
export const useVetDocumentInfo = () => {
  // Contextos necessários
  const { organization } = useOrganizationContext();
  const { location } = useLocationContext();
  const { user } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { petData, tutorData, appointment } = useVetAttendance();

  // Busca a referência da localização do appointment
  const locationReference = useFhirData.findParticipant(
    appointment?.participant,
    "Location"
  );

  // Encontra a localização selecionada
  const selectedLocation = location?.find((el: any) =>
    el?.resource?.id?.includes(locationReference?.reference.split("/")[1])
  );

  // Extrai informações do pet do petData
  const petInfo: PetInfo = {
    id: petData?.id || "",
    name: petData?.name?.[0]?.text || "",
    species:
      petData?.extension?.[0]?.extension?.[0]?.valueCodeableConcept?.coding?.[0]
        ?.display || "",
    breed:
      petData?.extension?.[0]?.extension?.[1]?.valueCodeableConcept?.coding?.[0]
        ?.display || "",
    // age: petData?.birthDate
    //   ? `${useFormatter.formatAge(`${petData.birthDate}`)} anos`
    //   : undefined,
  };

  // Extrai informações do tutor do tutorData
  const tutorInfo: TutorInfo = {
    name: tutorData?.name?.[0]?.text || "",
    document: tutorData?.identifier?.[0]?.value || "",
    phone: tutorData?.telecom?.[0]?.value || "",
  };

  // Extrai informações da clínica dos contextos
  const clinicInfo: ClinicInfo = {
    clinicId: organization?.id || "",
    clinicName: organization?.name || organization?.alias?.[0] || "",
    clinicAddress: selectedLocation?.resource?.address?.line?.join(", ") || "",
    clinicPhone: useFormatter.formatPhoneNumber(
      selectedLocation?.resource?.contact?.[0]?.telecom?.[0]?.value ||
        user?.phone ||
        ""
    ),
    vetName: user?.name || "",
    vetCRMV: userPractitionerRole?.identifier?.[0]?.value || "",
  };

  return {
    petInfo,
    tutorInfo,
    clinicInfo,
  };
};
